'use client'
import React, { useState, useCallback, useMemo } from 'react'
import { usePathname } from 'next/navigation'
import { useCinematicsStore } from '@/cinematics/stores/CinematicsStore'
import { IBanner } from '@/types/banners'
import { ISpotlightConfig } from '@/types/cinematics'
import {
  getEligibleBanners,
  hasPlanRule,
  hasGeoRule,
  hasOrderStatusRule,
  shouldRenderBannerStrip
} from '@/utils/banners/getEligibleBanners'
import { isBannerDismissed } from '@/hooks/banners/useBanner'
import { useUser } from '@/hooks/users/useUser'
import { useOrders } from '@/hooks/commerce/useOrders'
import { useGeolocation } from '@/hooks/geolocation/useGeolocation'
import { Navbar } from '@/components/UI/Navbar'
import { Banner } from '@/components/UI/Banner'
import { ConfigurableModals } from '@/components/UI/ConfigurableModals'
import { Filters } from '@/components/UI/Filters'
import { Footer } from '@/components/UI/Footer'
import { SubFooter } from '@/components/UI/SubFooter'
import { Sponsors } from '@/components/UI/Sponsors'
import { MinimalNavbar } from '@/components/UI/MinimalNavbar'
import { NAVBAR_TYPES } from '@/constants/navbar'
import { Spotlight } from '@/components/Spotlight'
import { useSpotlightQueue } from '@/hooks/spotlights/useSpotlightQueue'
import { FieldHighlightProvider } from '@/components/Inputs/FieldHighlight'

import classNames from 'classnames'
import classes from './styles.module.scss'

interface ILayoutProps {
  children?: React.ReactNode
  hasNavbar?: boolean
  hasFooter?: boolean
  hasSponsors?: boolean
  hasSubFooter?: boolean
  hasMarginX?: boolean
  hasMarginY?: boolean
  hasMinimalNavbar?: boolean
  isFullWidth?: boolean
}

export const Layout = (props: ILayoutProps) => {
  const { children } = props
  const { scene = {}, props: globalProps }: any = useCinematicsStore()
  const { isLoading: isLoadingProfile, isLoggedIn } = useUser()
  const pathname = usePathname()
  const set = scene?.set

  const globalBanners: IBanner[] = globalProps?.banners || []
  // Orders are only fetched when some banner actually carries a rule that
  // reads them — a plan rule or an order-state rule.
  const needsOrders = globalBanners.some((banner) => hasPlanRule(banner) || hasOrderStatusRule(banner))
  const { orders } = useOrders({ enabled: isLoggedIn && needsOrders })

  // Geolocation is only fetched when some banner actually carries a geo rule.
  const needsGeo = globalBanners.some(hasGeoRule)
  const { country, isLoading: isGeoLoading } = useGeolocation({ enabled: needsGeo })

  // Bumped when a banner is dismissed so selection re-runs and the freed
  // visibility slot goes to the next eligible banner.
  const [dismissedGeneration, setDismissedGeneration] = useState(0)
  const handleBannerDismissed = useCallback(() => {
    setDismissedGeneration((generation) => generation + 1)
  }, [])

  const navbarType = (set?.navbar?.type === NAVBAR_TYPES.minimal || props.hasMinimalNavbar)
    ? NAVBAR_TYPES.minimal
    : (set?.navbar?.type ?? NAVBAR_TYPES.standard)
  const hasNavbar = set?.navbar?.hasNavbar ?? props.hasNavbar ?? true
  const hasFooter = set?.footer?.hasFooter ?? props.hasFooter ?? true
  const hasMarginX = set?.margin?.hasMarginX ?? props.hasMarginX ?? true
  const hasMarginY = set?.margin?.hasMarginY ?? props.hasMarginY
  const hasFilters = set?.filters?.hasFilters ?? false
  const hasSponsors = set?.sponsors?.hasSponsors ?? false
  const hasSubFooter = set?.subfooter?.hasSubfooter ?? globalProps?.subfooter?.hasSubfooter ?? false
  const isFullWidth = set?.margin?.isFullWidth ?? props.isFullWidth ?? false
  const bannerIds = set?.banners?.ids
  const bannerMaxVisible = set?.banners?.maxVisible
  const hasSceneBanners = set?.banners?.hasBanners
  const banners = useMemo(() => getEligibleBanners(globalBanners, {
    ids: bannerIds,
    maxVisible: bannerMaxVisible,
    hasSceneBanners,
    pathname,
    isLoggedIn,
    orders,
    // Anonymous users hold no plans, so plan rules resolve immediately;
    // logged-in users wait for the (possibly cached) orders data.
    ordersReady: !isLoggedIn || !!orders,
    country,
    // Geo-ruled banners wait until geolocation settles (resolved or errored);
    // when no banner needs geo the gate is open so nothing is held back.
    geoReady: !needsGeo || !isGeoLoading,
    isDismissed: isBannerDismissed,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dismissedGeneration re-runs selection after a dismissal frees a slot
  }), [globalBanners, bannerIds, bannerMaxVisible, hasSceneBanners, pathname, orders, isLoggedIn, country, needsGeo, isGeoLoading, dismissedGeneration])
  // The strip gate has no "something was selected" term of its own, so the
  // emptiness check stays here — an empty strip must never render.
  const hasBanners = shouldRenderBannerStrip(set?.banners, banners) && banners.length > 0
  // A configurable modal and the spotlight wizard both claim the whole screen,
  // and neither knew about the other: a mobile-only-plan user without a phone
  // got the plan reminder and the phone prompt stacked on Home (PRO-1057). The
  // wizard waits, and resumes where it was once the modal closes.
  const [visibleModalIds, setVisibleModalIds] = useState<ReadonlySet<string>>(() => new Set())
  const handleModalVisibilityChange = useCallback((modalId: string, isVisible: boolean) => {
    setVisibleModalIds((current) => {
      if (current.has(modalId) === isVisible) return current
      const next = new Set(current)
      if (isVisible) next.add(modalId)
      else next.delete(modalId)
      return next
    })
  }, [])
  const isModalOpen = visibleModalIds.size > 0

  const hasSpotlights = set?.spotlights?.hasSpotlights ?? false
  const spotlights: ISpotlightConfig[] = globalProps?.spotlights || []
  const {
    activeSpotlight,
    hasNextStep,
    reportShown,
    skipActive,
    completeActive,
    dismissActive,
  } = useSpotlightQueue({
    spotlights,
    enabled: hasSpotlights && isLoggedIn && !isLoadingProfile && !isModalOpen,
  })

  return (
    // Any page can be deep-linked to one of its fields with `?highlight=<name>`.
    <FieldHighlightProvider>
      <div id='layout'>
        <ConfigurableModals
          modals={globalProps?.modals}
          stores={globalProps?.stores}
          onVisibilityChange={handleModalVisibilityChange}
        />
        <div className={classNames({
          [classes.body]: true,
          [classes['body--full-width']]: isFullWidth
        })}>
          <header className={classes.header}>
            {hasBanners &&
              banners.map((banner: IBanner) => (
                <Banner
                  key={banner?._id}
                  bannerProps={banner}
                  enabled={!isLoadingProfile}
                  onDismissed={handleBannerDismissed}
                />
              ))}
            {hasNavbar &&
              <>
                {navbarType === NAVBAR_TYPES.standard &&
                  <Navbar
                    setNavbarProps={set?.navbar}
                    globalNavbarProps={globalProps?.navbar}
                    searchProps={globalProps?.search}
                  />
                }
                {navbarType === NAVBAR_TYPES.minimal &&
                  <MinimalNavbar action={set?.navbar?.action} />
                }
              </>
            }
            {hasFilters &&
              <Filters
                globalFilterProps={globalProps?.filters}
                setFilterProps={set?.filters}
              />
            }
          </header>
          <main className={classNames({
            [classes.main]: true,
            [classes.marginX]: hasMarginX,
            [classes.marginY]: hasMarginY,
          })}
          >
            {children}
          </main>
          {/* One wizard step at a time; the queue owns ordering and persistence. */}
          {activeSpotlight &&
            <Spotlight
              // Keyed so advancing to the next step remounts and re-measures
              // instead of showing new copy over the previous target's cutout.
              key={activeSpotlight.storageKey}
              targetId={activeSpotlight.targetId}
              title={activeSpotlight.title}
              description={activeSpotlight.description}
              actionText={activeSpotlight.actionText}
              dismissText={activeSpotlight.dismissText}
              hasNextStep={hasNextStep}
              onShown={reportShown}
              onUnavailable={skipActive}
              onAction={completeActive}
              onDismiss={dismissActive}
            />
          }
        </div>
        <div className={classes.footer}>
          {hasSponsors &&
            <Sponsors
              setSponsorsProps={set?.sponsors}
              globalSponsorsProps={globalProps?.sponsors}
            />
          }
          {hasFooter &&
            <Footer
              setFooterProps={set?.footer}
              globalFooterProps={globalProps?.footer}
              globalStoresProps={globalProps?.stores}
            />
          }
          {hasSubFooter &&
            <SubFooter
              globalSubFooterProps={globalProps?.subfooter}
              setSubFooterProps={set?.subfooter}
            />
          }
        </div>
      </div>
    </FieldHighlightProvider>
  )
}